/**
 * Study destinations offered in the wizard and the routes filter (T-I18N-01).
 *
 * A curated list, not the full ISO 3166 set: the profile question is "where
 * are you considering?", and 250 options — most of which no route in the
 * catalogue serves — is a worse answer than 45 that do.
 *
 * English and Russian names come from `Intl.DisplayNames`, which every
 * target browser ships. Uzbek is not reliably available in ICU data, so it
 * is a table here; a missing entry falls back to the English name rather
 * than to a bare country code.
 */
import type {Locale} from './api/types'

export const COUNTRY_CODES = [
  'AT', 'AU', 'AZ', 'BE', 'CA', 'CH', 'CN', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GB', 'GR', 'HU', 'ID', 'IE', 'IN', 'IT', 'JP', 'KR', 'KZ', 'LT', 'LV',
  'MY', 'NL', 'NO', 'NZ', 'PL', 'PT', 'RO', 'RU', 'SA', 'SE', 'SG', 'SK', 'TH',
  'TR', 'TW', 'UA', 'US', 'UZ', 'AE'
] as const

export type CountryCode = (typeof COUNTRY_CODES)[number]

/**
 * Every country the university catalogue holds: where the QS World University
 * Rankings lists at least one university. Wider than `COUNTRY_CODES`, which
 * stays the short list the profile asks about. Regenerate from `data/qs/`
 * when a new edition adds a country.
 */
export const CATALOGUE_COUNTRY_CODES: readonly string[] = [
  'AE', 'AM', 'AR', 'AT', 'AU', 'AZ', 'BA', 'BD', 'BE', 'BG', 'BH', 'BN',
  'BR', 'BY', 'CA', 'CH', 'CL', 'CN', 'CO', 'CR', 'CU', 'CY', 'CZ', 'DE',
  'DK', 'DO', 'EC', 'EE', 'EG', 'ES', 'ET', 'FI', 'FR', 'GB', 'GE', 'GH',
  'GR', 'GT', 'HK', 'HN', 'HR', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IR',
  'IS', 'IT', 'JO', 'JP', 'KE', 'KG', 'KR', 'KW', 'KZ', 'LB', 'LK', 'LT',
  'LU', 'LV', 'MA', 'MO', 'MT', 'MX', 'MY', 'NG', 'NL', 'NZ', 'OM', 'PA',
  'PE', 'PH', 'PK', 'PL', 'PR', 'PS', 'PT', 'PY', 'QA', 'RO', 'RS', 'RU',
  'SA', 'SD', 'SE', 'SG', 'SI', 'SK', 'SY', 'TH', 'TN', 'TR', 'TW', 'TZ',
  'UA', 'UG', 'US', 'UY', 'UZ', 'VE', 'VN', 'ZA'
]

const UZBEK_NAMES: Record<string, string> = {
  AT: 'Avstriya', AU: 'Avstraliya', AZ: 'Ozarbayjon', BE: 'Belgiya', CA: 'Kanada',
  CH: 'Shveytsariya', CN: 'Xitoy', CZ: 'Chexiya', DE: 'Germaniya', DK: 'Daniya',
  EE: 'Estoniya', ES: 'Ispaniya', FI: 'Finlyandiya', FR: 'Fransiya',
  GB: 'Buyuk Britaniya', GR: 'Gretsiya', HU: 'Vengriya', ID: 'Indoneziya',
  IE: 'Irlandiya', IN: 'Hindiston', IT: 'Italiya', JP: 'Yaponiya',
  KR: 'Janubiy Koreya', KZ: 'Qozog‘iston', LT: 'Litva', LV: 'Latviya',
  MY: 'Malayziya', NL: 'Niderlandiya', NO: 'Norvegiya', NZ: 'Yangi Zelandiya',
  PL: 'Polsha', PT: 'Portugaliya', RO: 'Ruminiya', RU: 'Rossiya',
  SA: 'Saudiya Arabistoni', SE: 'Shvetsiya', SG: 'Singapur', SK: 'Slovakiya',
  TH: 'Tailand', TR: 'Turkiya', TW: 'Tayvan', UA: 'Ukraina', US: 'AQSh',
  UZ: 'O‘zbekiston', AE: 'BAA',
  AM: 'Armaniston', AR: 'Argentina', BA: 'Bosniya va Gersegovina', BD:
  'Bangladesh', BG: 'Bolgariya', BH: 'Bahrayn', BN: 'Bruney', BR: 'Braziliya',
  BY: 'Belarus', CL: 'Chili', CO: 'Kolumbiya', CR: 'Kosta-Rika', CU: 'Kuba',
  CY: 'Kipr', DO: 'Dominikan Respublikasi', EC: 'Ekvador', EG: 'Misr', ET:
  'Efiopiya', GE: 'Gruziya', GH: 'Gana', GT: 'Gvatemala', HK: 'Gonkong', HN:
  'Gonduras', HR: 'Xorvatiya', IL: 'Isroil', IQ: 'Iroq', IR: 'Eron', IS:
  'Islandiya', JO: 'Iordaniya', KE: 'Keniya', KG: 'Qirg‘iziston', KW:
  'Quvayt', LB: 'Livan', LK: 'Shri-Lanka', LU: 'Lyuksemburg', MA: 'Marokash',
  MO: 'Makao', MT: 'Malta', MX: 'Meksika', NG: 'Nigeriya', OM: 'Ummon', PA:
  'Panama', PE: 'Peru', PH: 'Filippin', PK: 'Pokiston', PR: 'Puerto-Riko', PS:
  'Falastin', PY: 'Paragvay', QA: 'Qatar', RS: 'Serbiya', SD: 'Sudan', SI:
  'Sloveniya', SY: 'Suriya', TN: 'Tunis', TZ: 'Tanzaniya', UG: 'Uganda', UY:
  'Urugvay', VE: 'Venesuela', VN: 'Vyetnam', ZA: 'Janubiy Afrika'
}

const INTL_LOCALE: Record<Locale, string> = {uz: 'en', en: 'en', ru: 'ru'}

export function countryName(code: string, locale: Locale): string {
  if (locale === 'uz' && UZBEK_NAMES[code]) return UZBEK_NAMES[code]
  try {
    const display = new Intl.DisplayNames([INTL_LOCALE[locale] ?? 'en'], {type: 'region'})
    return display.of(code) ?? code
  } catch {
    return UZBEK_NAMES[code] ?? code
  }
}

/**
 * Several countries for one line of a card: the first few by name, then a
 * count, so an award open across Europe does not push everything else off
 * the card (ING-11).
 */
export function countryList(codes: string[], locale: Locale, shown = 3): string {
  const names = codes.slice(0, shown).map((code) => countryName(code, locale))
  const rest = codes.length - names.length
  return rest > 0 ? `${names.join(', ')} +${rest}` : names.join(', ')
}

/** The offered countries (the profile's short list unless told otherwise), sorted by localized name. */
export function countryOptions(
  locale: Locale,
  codes: readonly string[] = COUNTRY_CODES
): {code: string; name: string}[] {
  return codes.map((code) => ({code, name: countryName(code, locale)})).sort((a, b) =>
    a.name.localeCompare(b.name, locale === 'uz' ? 'en' : locale)
  )
}
